import React from 'react';

/**
 * The chat's icon set: one stroke weight, round joins, 24-unit grid. Drawn here so every glyph on the
 * screen shares the same hand; never emoji, never a mixed library.
 */

type IconProps = React.SVGProps<SVGSVGElement>;

const base = (props: IconProps): IconProps => ({
   viewBox: '0 0 24 24',
   fill: 'none',
   stroke: 'currentColor',
   strokeWidth: 1.75,
   strokeLinecap: 'round',
   strokeLinejoin: 'round',
   'aria-hidden': true,
   focusable: false,
   ...props
});

/** Send: an arrow up, the way a note goes up to the desk. */
export const ArrowUpIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
   </svg>
);

/** Speak: a microphone on its stand, for a question asked aloud. */
export const MicIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
      <path d="M12 18v2.5" />
   </svg>
);

/** Onward: a chevron to the end side, for the next photo; turned for the previous one, and both mirror right-to-left. */
export const ChevronIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M9.5 5.5l6.5 6.5-6.5 6.5" />
   </svg>
);

export const CloseIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
   </svg>
);

export const PhoneIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M5 4h3.5l1.5 4-2 1.5a12 12 0 0 0 6.5 6.5L16 14l4 1.5V19a1.5 1.5 0 0 1-1.5 1.5C10.5 20.5 3.5 13.5 3.5 5.5A1.5 1.5 0 0 1 5 4z" />
   </svg>
);

export const MailIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="M4 7l8 6 8-6" />
   </svg>
);

/** Sources: the pages of the directory. */
export const PagesIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M7 4.5h8.5L19 8v11.5H7z" />
      <path d="M15.5 4.5V8H19" />
      <path d="M10 12h6M10 15.5h6" />
   </svg>
);

/** Reception: a desk bell. */
export const BellIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M4.5 16.5h15" />
      <path d="M6 16.5a6 6 0 0 1 12 0" />
      <path d="M12 10.5V8" />
      <path d="M10.5 8h3" />
      <path d="M3.5 19.5h17" />
   </svg>
);

export const RetryIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M19 12a7 7 0 1 1-2.05-4.95" />
      <path d="M19 4v4h-4" />
   </svg>
);

/** Something went wrong: a ring with a mark. Persimmon carries the colour; the glyph carries the meaning. */
export const AlertIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 8v4.5" />
      <path d="M12 15.5h.01" strokeWidth={2.25} />
   </svg>
);

/** A notice: a ring with an i. */
export const InfoIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" strokeWidth={2.25} />
   </svg>
);

/** A broken link, for the invalid-link notice. */
export const LinkOffIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M9.5 6.5 11 5a4 4 0 0 1 5.66 5.66L15 12.3" />
      <path d="M14.5 17.5 13 19a4 4 0 0 1-5.66-5.66L9 11.7" />
      <path d="M4 4l16 16" />
   </svg>
);

/** A photo: a frame with a hill and the sun, for a photo that could not be shown. */
export const PhotoIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <path d="M3.5 16l5-5 4 4 2.5-2.5 5.5 5.5" />
      <circle cx="15.5" cy="9.5" r="1.25" />
   </svg>
);

export const SunIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <circle cx="12" cy="12" r="3.75" />
      <path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M16.6 16.6 18 18M6 18l1.4-1.4M16.6 7.4 18 6" />
   </svg>
);

export const MoonIcon: React.FC<IconProps> = (props) => (
   <svg {...base(props)}>
      <path d="M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10z" />
   </svg>
);

/** The wait ring: a hairline track and a sky arc that turns (CSS animates the arc). */
export const WaitRing: React.FC<{ className?: string }> = ({ className }) => (
   <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <circle className="cx-ring__track" cx="8" cy="8" r="6.25" strokeWidth="1.5" />
      <path className="cx-ring__arc" d="M8 1.75a6.25 6.25 0 0 1 6.25 6.25" strokeWidth="1.5" strokeLinecap="round" />
   </svg>
);
